import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  DATE_AGGREGATION_MONTH,
  DATE_AGGREGATION_WEEK,
  type DateAggregation,
  HISTORICAL_POINTS_AMOUNT,
} from 'lumen/utils/constants/pi-constant-values';
import getTodayUtc from 'lumen/utils/dates/get-today-utc';

// Parity Insight way to determine the last possible extract date (= last Monday)
// Last week's full dataset is returned starting from Monday at 5PM UTC
export function getLastPossibleExtractDate(): Dayjs {
  return dayjs().utc().subtract(1, 'week').startOf('isoWeek');
}

// Parity insight way to guess the last possible extract date (last Monday)
// Last week's full dataset is returned starting from Monday at 5PM UTC
export function getFormattedLastPossibleExtractDate(): string {
  return getLastPossibleExtractDate().format('YYYY-MM-DD');
}

export function getFirstHistoricalExtractDateInRange(
  historicalExtractDate: Dayjs,
  dateAggregation: DateAggregation,
): Dayjs {
  const isLatest = historicalExtractDate.isAfter(getLastPossibleExtractDate());
  const historicalExtractDateDay = isLatest
    ? getLastPossibleExtractDate()
    : historicalExtractDate;

  return historicalExtractDateDay
    .subtract(HISTORICAL_POINTS_AMOUNT - 1, dateAggregation)
    .startOf(dateAggregation);
}

export function getFirstFormattedHistoricalExtractDateInRange(
  historicalExtractDate: Dayjs,
  dateAggregation: DateAggregation,
): string {
  return getFirstHistoricalExtractDateInRange(
    historicalExtractDate,
    dateAggregation,
  ).format('YYYY-MM-DD');
}

export function isHistoryNearEnd(
  firstExtractDate: string,
  historicalExtractDate: string,
  dateAggregation: DateAggregation = DATE_AGGREGATION_WEEK,
): boolean {
  const firstAllowedHistoricalExtractDate =
    getFirstAllowedHistoricalExtractDate(firstExtractDate, dateAggregation);

  return dayjs(historicalExtractDate).isBefore(
    firstAllowedHistoricalExtractDate,
    dateAggregation,
  );
}

export function getFirstAllowedHistoricalExtractDate(
  firstExtractDate: Parameters<typeof dayjs>[0],
  dateAggregation: dayjs.ManipulateType = DATE_AGGREGATION_WEEK,
): Dayjs {
  const firstPossible = dayjs(firstExtractDate)
    .add(HISTORICAL_POINTS_AMOUNT - 1, dateAggregation)
    .startOf('isoWeek');

  const today = getTodayUtc();
  // Safely handle case where first possible is beyond last possible (no-future-dates)
  if (firstPossible.isAfter(today)) {
    return dayjs(today);
  }

  return firstPossible;
}

export function isExtractDateOutsideHistory(
  extractDate: Parameters<typeof dayjs>[0],
  historicalExtractDate: Parameters<typeof dayjs>[0],
  dateAggregation: dayjs.ManipulateType = DATE_AGGREGATION_WEEK,
): boolean {
  const lastPointInHistory = dayjs(historicalExtractDate).subtract(
    HISTORICAL_POINTS_AMOUNT - 1,
    dateAggregation,
  );
  const firstPointInHistory = dayjs(historicalExtractDate);
  const extractDateMoment = dayjs(extractDate);

  return (
    extractDateMoment.isBefore(lastPointInHistory, dateAggregation) ||
    extractDateMoment.isAfter(firstPointInHistory, dateAggregation)
  );
}

export function parityAnalyticsDateFormat(
  date: Dayjs,
  dateAggregation: DateAggregation,
): string {
  if (dateAggregation === DATE_AGGREGATION_MONTH) {
    return date.format('MMMM');
  }

  return date.format('ll');
}
